import React, { useCallback, useEffect, useState } from "react";
import SoftphoneController from "../../controllers/SoftphoneController";
import Spinner from "../Loading/loadingSpinner/Spinner";
import { colors } from "../../propsType/Colors";

const IOS_STORE_URL = "https://apps.apple.com/us/app/netuno-voz/id6766534600";
const ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=com.netuno.netunovoz.android";

const initialAsync = { status: "loading", data: null, errorMsg: "" };
const MISSING_CONTRATO_MSG = "No pudimos obtener tu contrato para generar el QR. Intentalo mas tarde.";

const SoftphoneCard = ({ contrato, cuenta, token }) => {
    const [qrState, setQrState] = useState(initialAsync);
    const [linkState, setLinkState] = useState(initialAsync);

    const fetchQR = useCallback(async () => {
        if (!contrato || !cuenta || !token) {
            setQrState({ status: "error", data: null, errorMsg: MISSING_CONTRATO_MSG });
            return;
        }
        setQrState(initialAsync);
        try {
            const res = await SoftphoneController.getSoftphoneQR({ contrato, cuenta, token });
            if (res?.qrBase64) {
                setQrState({ status: "ready", data: res.qrBase64, errorMsg: "" });
            } else {
                setQrState({ status: "error", data: null, errorMsg: res?.message || "No se pudo generar el QR." });
            }
        } catch (err) {
            setQrState({ status: "error", data: null, errorMsg: err?.message || "No se pudo generar el QR." });
        }
    }, [contrato, cuenta, token]);

    const fetchLink = useCallback(async () => {
        if (!contrato || !cuenta || !token) {
            setLinkState({ status: "error", data: null, errorMsg: MISSING_CONTRATO_MSG });
            return;
        }
        setLinkState(initialAsync);
        try {
            const res = await SoftphoneController.getSoftphoneLink({ contrato, cuenta, token });
            if (res?.link) {
                setLinkState({ status: "ready", data: res.link, errorMsg: "" });
            } else {
                setLinkState({ status: "error", data: null, errorMsg: res?.message || "No se pudo obtener el enlace." });
            }
        } catch (err) {
            setLinkState({ status: "error", data: null, errorMsg: err?.message || "No se pudo obtener el enlace." });
        }
    }, [contrato, cuenta, token]);

    useEffect(() => {
        fetchQR();
        fetchLink();
    }, [fetchQR, fetchLink]);

    const handleOpenLink = () => {
        if (linkState.status === "ready" && linkState.data) {
            window.location.href = linkState.data;
        }
    };

    return (
        <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">
                Descarga NetUno Voz en:
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-[1fr_1.2fr] place-content-center gap-2 mb-5">
                <a
                    href={IOS_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Descarga Cloud Softphone en App Store"
                    className="flex justify-center items-center gap-1.5 bg-black text-white rounded-lg py-2 px-2 text-sm font-semibold hover:bg-gray-800 duration-300 easy"
                >
                    <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                    </svg>
                    <span className="flex flex-col items-start leading-tight whitespace-nowrap">
                        <span className="text-[10px] font-normal">Descargar en</span>
                        <span className="text-sm font-semibold">App Store</span>
                    </span>
                </a>
                <a
                    href={ANDROID_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Descarga Cloud Softphone en Google Play"
                    className="flex justify-center items-center gap-1.5  bg-black text-white rounded-lg py-2 px-2 text-sm font-semibold hover:bg-gray-800 duration-300 easy"
                >
                    <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#4285F4" d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z"/>
                        <path fill="#34A853" d="M14.42 11.378L4.83 1.4a1 1 0 0 1 .918-.018l11.997 6.823-3.325 3.173z"/>
                        <path fill="#EA4335" d="M14.42 12.622l-9.59 9.978a1 1 0 0 0 .918.018l11.997-6.823-3.325-3.173z"/>
                        <path fill="#FBBC04" d="M17.745 8.205l-3.325 3.173 3.325 3.173 3.027-1.722a1.5 1.5 0 0 0 0-2.902l-3.027-1.722z"/>
                    </svg>
                    <span className="flex flex-col items-start leading-tight whitespace-nowrap">
                        <span className="text-[10px] font-normal">Descargar en</span>
                        <span className="text-sm font-semibold">Google Play</span>
                    </span>
                </a>
            </div>

            <div className="flex flex-col items-center bg-gray-50 rounded-lg py-4 px-3 mb-4 min-h-[200px] justify-center">
                {qrState.status === "loading" && <Spinner />}
                {qrState.status === "ready" && (
                    <img
                        src={`data:image/png;base64,${qrState.data}`}
                        alt="Codigo QR para configurar NetUno Voz"
                        className="w-40 h-40 object-contain"
                    />
                )}
                {qrState.status === "error" && (
                    <div className="flex flex-col items-center text-center gap-2">
                        <p className="text-sm text-gray-500">{qrState.errorMsg}</p>
                        {qrState.errorMsg !== MISSING_CONTRATO_MSG && (
                            <button
                                type="button"
                                onClick={fetchQR}
                                className={`text-sm font-medium text-[${colors.lightBlue}] hover:underline`}
                            >
                                Reintentar
                            </button>
                        )}
                    </div>
                )}
                {qrState.status === "ready" && (
                    <p className="mt-3 text-xs text-gray-500 text-center">
                        Escanea el QR desde la App NetUno Voz
                    </p>
                )}
            </div>

            <button
                type="button"
                onClick={handleOpenLink}
                disabled={linkState.status !== "ready"}
                className={`w-full flex justify-center items-center text-sm font-medium text-center rounded-lg px-4 py-3 duration-300 easy
                    ${linkState.status === "ready"
                        ? `bg-[${colors.lightBlue}] text-white hover:bg-[${colors.darkBlue}] cursor-pointer`
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
            >
                {linkState.status === "loading" && "Cargando enlace..."}
                {linkState.status === "ready" && "Abrir en mi app"}
                {linkState.status === "error" && "Enlace no disponible"}
            </button>
            {linkState.status === "error" && (
                <div className="mt-2 flex flex-col justify-center items-center gap-2 text-xs text-gray-500">
                    <span>{linkState.errorMsg}</span>
                    {linkState.errorMsg !== MISSING_CONTRATO_MSG && (
                        <button
                            type="button"
                            onClick={fetchLink}
                            className={`font-medium text-[${colors.lightBlue}] hover:underline`}
                        >
                            Reintentar
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default SoftphoneCard;
